import LFCSelectFormDeviceEx from '_components-with-data/inputs/LFCSelectFormDeviceEx';
import LFCSelectFormDongleEx from '_components-with-data/inputs/LFCSelectFormDongleEx';
import LFCSelectFormWorkEx from '_components-with-data/inputs/LFCSelectFormWorkEx';
import LFCNumberField from '_components/inputs/LFCNumberField';
import LFCSelectForm from '_components/inputs/LFCSelectForm';
import LFCTimePicker from '_components/inputs/LFCTimePicker';
import LFCSavedCondition, {ConditionParamer} from '_components/search-conditions/LFCSavedCondition';
import LFCSideMenu from '_components/search-conditions/LFCSideMenu';
import {useGetDefaultCondition} from '_contexts/SavedConditionProvider';
import {
  LOCATIONID_LQ_INSPECTION_PROGRESS_MONITOR_MAIN,
  PAGEID_LQ_INSPECTION_PROGRESS_MONITOR
} from '_logics/LFCPageId';
import {handleInputChange, handleInputChange2} from '_logics/LFCUtil';
import {useCallback, useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {Box, Grid, Typography} from '@mui/material';

import {SearchConditions} from './types';

const WORKING_TIME = [...Array(24)].map((_, i) => {
  return {name: String(i + 1), value: i + 1};
});

interface SearchPanelProps {
  onSearch: (searchConditions: SearchConditions) => void;
  setDelay: React.Dispatch<React.SetStateAction<number | null>>;
}

export const SearchPanel = (props: SearchPanelProps) => {
  const {t} = useTranslation();
  const formRef = useRef<HTMLFormElement>(null!);
  const getDefaultCondition = useGetDefaultCondition();

  //入力中条件
  const [searchConditions, setSearchConditions] = useState<SearchConditions>(
    getDefaultCondition(
      PAGEID_LQ_INSPECTION_PROGRESS_MONITOR,
      LOCATIONID_LQ_INSPECTION_PROGRESS_MONITOR_MAIN
    ) ?? {
      select_time: '00:00',
      working_time: 24,
      target_number: '1000',
      work: [],
      deviceid: [],
      dongleid: []
    }
  );

  /**
   * 検索処理
   */
  const onSearch = useCallback(() => {
    if (!formRef.current?.reportValidity()) {
      return;
    }
    props.onSearch(searchConditions);
    return true;
  }, [searchConditions, props]);

  // 検索条件保存関連
  const [openSavedCondition, setOpenSavedCondition] = useState(false);
  const [condition, setCondition] = useState<ConditionParamer[]>([]);

  const onLoadSavedCondition = () => {
    if (!formRef.current?.reportValidity()) {
      return;
    }
    setCondition([
      {
        name: 'select_time',
        value: searchConditions.select_time,
        valueLabel: searchConditions.select_time,
        colName: t('稼働開始時刻'),
        colWidth: 100
      },
      {
        name: 'working_time',
        value: searchConditions.working_time,
        valueLabel: String(searchConditions.working_time),
        colName: t('1日の稼働時間(h)'),
        colWidth: 120
      },
      {
        name: 'target_number',
        value: searchConditions.target_number,
        valueLabel: searchConditions.target_number,
        colName: t('目標生産数'),
        colWidth: 100
      },
      {
        name: 'work',
        value: searchConditions.work,
        valueLabel: searchConditions.work.join(','),
        colName: t('機種'),
        colWidth: 300
      },
      {
        name: 'deviceid',
        value: searchConditions.deviceid,
        valueLabel: searchConditions.deviceid.join(','),
        colName: t('検査装置'),
        colWidth: 300
      },
      {
        name: 'dongleid',
        value: searchConditions.dongleid,
        valueLabel: searchConditions.dongleid.join(','),
        colName: t('オペレータ'),
        colWidth: 300
      }
    ]);
    setOpenSavedCondition(true);
  };

  const onSelectSavedCondition = (conditionValues: any) => {
    setSearchConditions({...searchConditions, ...conditionValues});
    setOpenSavedCondition(false);
  };

  /**
   * 初期処理
   */
  useEffect(() => {
    onSearch();
  }, []);

  return (
    <form ref={formRef}>
      <LFCSideMenu onOpen={() => props.setDelay(null)} onClose={() => onSearch()}>
        <Box m={2} sx={{width: 240}}>
          <Grid container spacing={1}>
            <Grid item xs={12} mb={1}>
              <Typography color="textSecondary" variant="subtitle1">
                {t('抽出条件')}
              </Typography>
            </Grid>
            <Grid item xs={12} pb={1}>
              <LFCTimePicker
                name={'select_time'}
                label={t('稼働開始時刻')}
                value={searchConditions.select_time}
                onChange={event => handleInputChange(event, searchConditions, setSearchConditions)}
                required
                fullWidth
              />
            </Grid>
            <Grid item xs={12} pb={1}>
              <LFCSelectForm
                name={'working_time'}
                label={t('1日の稼働時間(h)')}
                value={searchConditions.working_time}
                id={'working_time'}
                onChange={event => handleInputChange2(event, searchConditions, setSearchConditions)}
                selectItem={WORKING_TIME}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} pb={1}>
              <LFCNumberField
                name={'target_number'}
                label={t('目標生産数')}
                value={searchConditions.target_number}
                onChange={event => handleInputChange(event, searchConditions, setSearchConditions)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} pb={1}>
              <LFCSelectFormWorkEx
                name={'work'}
                value={searchConditions.work}
                onChange={relayDatas =>
                  setSearchConditions(prev => ({...prev, [relayDatas.name]: relayDatas.data}))
                }
                multiple={true}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} pb={1}>
              <LFCSelectFormDeviceEx
                name={'deviceid'}
                value={searchConditions.deviceid}
                onChange={relayDatas =>
                  setSearchConditions(prev => ({...prev, [relayDatas.name]: relayDatas.data}))
                }
                multiple={true}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} pb={1}>
              <LFCSelectFormDongleEx
                name={'dongleid'}
                value={searchConditions.dongleid}
                onChange={relayDatas =>
                  setSearchConditions(prev => ({...prev, [relayDatas.name]: relayDatas.data}))
                }
                multiple={true}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <LFCSavedCondition
                open={openSavedCondition}
                pageId={PAGEID_LQ_INSPECTION_PROGRESS_MONITOR}
                locationNo={LOCATIONID_LQ_INSPECTION_PROGRESS_MONITOR_MAIN}
                onLoad={onLoadSavedCondition}
                onSelect={onSelectSavedCondition}
                onClose={() => setOpenSavedCondition(false)}
                conditions={condition}
              />
            </Grid>
          </Grid>
        </Box>
      </LFCSideMenu>
    </form>
  );
};
